import { type Interpolation } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faEye, faEyeSlash, faKeyboard,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type AriaRole, type ChangeEvent, type FocusEvent, forwardRef, type HTMLAttributes, type KeyboardEventHandler,
  type MouseEvent, type ReactNode, useCallback, useRef, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { delay } from '~/_shared/utils/delay';
import { useTranslation } from '~/_shared/utils/hooks';
import { useCombinedRefs } from '~/_shared/utils/hooks/useCombinedRefs';
import { type autoCompleteDisabled } from '../../../utils/dom/dom.helpers';
import {
  additionalContentStyle, getIconStyles, inputStyles, showHideIconContainerStyle, wrapperStyle,
} from './textInput.styles';

export type InputType = 'email' | 'password' | 'text' | 'number';

export enum InputSize {
  Xs = 'Xs',
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large'
}

export type InputProps = Readonly<{
  autoComplete?: string | typeof autoCompleteDisabled;
  className?: string;
  icon?: IconProp | null;
  iconStyle?: Interpolation;
  id?: string;
  inputSize?: number;
  inputStyle?: Interpolation;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  isDisabled?: boolean;
  isReadOnly?: boolean;
  leftContent?: ReactNode;
  max?: number;
  min?: number;
  name?: string;
  placeholder?: string;
  rightContent?: ReactNode;
  role?: AriaRole;
  size?: InputSize;
  title?: string;
  type?: InputType;
  value?: string;
  isError?: boolean;
  style?: React.CSSProperties;

  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (newValue: string, event: ChangeEvent) => void;
  onClick?: (e?: MouseEvent) => void;
  onFocus?: () => void;
  onKeyUp?: KeyboardEventHandler;
}>;

export const TextInputComponent = forwardRef<HTMLInputElement, InputProps & Omit<HTMLAttributes<HTMLInputElement>, keyof InputProps>>(({
  autoComplete,
  className,
  icon = faKeyboard,
  iconStyle,
  id,
  inputMode,
  inputSize,
  inputStyle,
  isDisabled,
  isReadOnly,
  isError,
  leftContent,
  max,
  min,
  name,
  onBlur,
  onChange,
  onClick,
  onFocus,
  onKeyUp,
  placeholder,
  rightContent,
  role,
  size = InputSize.Medium,
  title,
  type = 'text',
  value,
  style,
  ...restProps
}, ref) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRefs = useCombinedRefs<HTMLInputElement>(inputRef, ref);
  const justClearedRef = useRef(false);
  const [t] = useTranslation();

  const hasShowHideIcon = type === 'password';

  const onToggleShowPasswordClick = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const oldValue = value ?? '';
    const newValue = e.target.value;
    const inputType = (e.nativeEvent as InputEvent)?.inputType;

    // Workaround for Chrome/Edge bug: Browser re-applies autofill after user clears
    // Detect unwanted autofill by checking for missing inputType after a clear action
    if (oldValue === '' && newValue !== '' && justClearedRef.current && !inputType) {
      // Missing inputType indicates Chrome autofill, block it
      justClearedRef.current = false;
      return;
    }

    // Track when field is cleared to detect aggressive autofill
    if (newValue === '' && oldValue !== '') {
      justClearedRef.current = true;
      delay(200).then(() => justClearedRef.current = false);
    }
    else if (inputType) {
      justClearedRef.current = false;
    }

    onChange?.(newValue, e);
  }, [onChange, value]);

  return (
    <div
      style={style}
      css={wrapperStyle({ size, isDisabled, isReadOnly, isError })}
      className={className}
      onClick={focusInput}
    >
      {icon !== null && (
        <span css={[getIconStyles(size), iconStyle]}>
          <FontAwesomeIcon icon={icon} />
        </span>
      )}

      <span css={additionalContentStyle}>
        {leftContent}
      </span>

      <input
        {...restProps}
        ref={inputRefs}
        css={[inputStyles, inputStyle]}
        id={id}
        type={hasShowHideIcon && showPassword ? 'text' : type}
        value={value}
        disabled={isDisabled}
        readOnly={isReadOnly}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={onBlur}
        title={title}
        onClick={onClick}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
        size={inputSize}
        min={min}
        max={max}
        autoComplete={autoComplete}
        role={role}
        name={name}
        inputMode={inputMode}
      />

      {hasShowHideIcon && (
        <div css={showHideIconContainerStyle({ size })}>
          <FontAwesomeIcon
            icon={showPassword ? faEyeSlash : faEye}
            onClick={onToggleShowPasswordClick}
            title={t('Toggle Show Password')}
          />
        </div>
      )}

      <span css={additionalContentStyle}>
        {rightContent}
      </span>
    </div>
  );
});

TextInputComponent.displayName = 'TextInputComponent';
